import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { env } from '@/config/env'
import { useAppUpdateStore } from '../stores/useAppUpdateStore'
import {
  CURRENT_BUILD_ID,
  UPDATE_STATUS_POLL_INTERVAL_MS,
  createNewBuildCheck,
  fetchUpdateStatus,
  pollUntilUpdated,
  updateStatusUrl,
  updatingHealthCheckUrl,
} from '../utils/appUpdate'
import { freezePage } from '../utils/freezePage'
import { APP_UPDATE_BANNER_HOST, AppUpdateBanner, UpdatingModal } from './AppUpdateNotices'

/** The router's state and subscription, enough to notice a route change. */
export interface RouteChanges {
  state: { location: unknown }
  subscribe(listener: (state: { location: unknown }) => void): () => void
}

function reloadPage() {
  window.location.reload()
}

type Fetch = typeof fetch

/**
 * Keeps an open tab on the live build.
 *
 * - Every 20 seconds while the tab is visible (and when it becomes visible
 *   again), the update notice (`GET /api/update-status`) is read: an update
 *   coming shows the "Updating soon" banner above the app header.
 * - While a deploy holds the app (any request met the API's updating 503, or
 *   the notice says so), the page freezes as it was (see `freezePage`), the
 *   updating modal covers it, and the API's health check is asked every 10
 *   seconds; when it answers, the page reloads onto the new build. Closing
 *   the modal shows the frozen page with the "Updates in progress" banner.
 * - When the tab becomes active again or the route changes (at most every
 *   30 seconds), `/version.json` is checked, and a tab on an older build
 *   reloads, even with unsaved input: a form from the old build could save
 *   against a changed schema.
 *
 * Without a build to compare (development), there's no notice to read.
 */
export function AppUpdateController({
  routeChanges,
  currentBuild = CURRENT_BUILD_ID,
  healthCheckUrl = updatingHealthCheckUrl(env.VITE_API_URL),
  statusUrl = updateStatusUrl(env.VITE_API_URL),
  reload = reloadPage,
  fetchImpl = (...args) => fetch(...args),
  statusIntervalMs = UPDATE_STATUS_POLL_INTERVAL_MS,
}: {
  routeChanges?: RouteChanges
  currentBuild?: string | null
  healthCheckUrl?: string
  statusUrl?: string
  reload?: () => void
  fetchImpl?: Fetch
  statusIntervalMs?: number
}) {
  const updating = useAppUpdateStore((state) => state.updating)
  const frozenPageShown = useAppUpdateStore((state) => state.frozenPageShown)

  useEffect(() => {
    if (!updating) return
    const thaw = freezePage()
    const stop = pollUntilUpdated({ url: healthCheckUrl, onDone: reload })
    return () => {
      stop()
      thaw()
    }
  }, [updating, healthCheckUrl, reload])

  useEffect(() => {
    if (!currentBuild || updating) return
    let stopped = false
    let inFlight: AbortController | undefined
    const read = async () => {
      if (document.visibilityState !== 'visible') return
      inFlight?.abort()
      inFlight = new AbortController()
      const status = await fetchUpdateStatus(statusUrl, fetchImpl, inFlight.signal)
      if (stopped) return
      const store = useAppUpdateStore.getState()
      if (status.state === 'updating') store.markUpdating(status.estimatedEndAt)
      else if (status.state === 'upcoming')
        store.setUpcoming({ startsAt: status.startsAt, downtimeSeconds: status.downtimeSeconds })
      else store.setUpcoming(null)
    }
    void read()
    const timer = setInterval(() => void read(), statusIntervalMs)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void read()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      stopped = true
      clearInterval(timer)
      inFlight?.abort()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [currentBuild, updating, statusUrl, fetchImpl, statusIntervalMs])

  const [checkForNewBuild] = useState(() =>
    createNewBuildCheck({
      currentBuild,
      onNewBuild: () => {
        // The updating modal's own wait ends in a reload already.
        if (!useAppUpdateStore.getState().updating) reload()
      },
    })
  )

  useEffect(() => {
    if (!currentBuild) return
    const check = () => {
      if (!useAppUpdateStore.getState().updating) void checkForNewBuild()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', check)
    let location = routeChanges?.state.location
    const unsubscribe = routeChanges?.subscribe((state) => {
      if (state.location === location) return
      location = state.location
      check()
    })
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', check)
      unsubscribe?.()
    }
  }, [currentBuild, checkForNewBuild, routeChanges])

  if (!updating) return null
  if (!frozenPageShown) {
    return createPortal(
      <div className="fixed inset-0 z-[var(--layer-app-update)]">
        <UpdatingModal />
      </div>,
      document.body
    )
  }
  // A page outside the app's shell has no header to sit above: its banner goes at the top.
  if (document.querySelector(`[${APP_UPDATE_BANNER_HOST}]`)) return null
  return createPortal(
    <div className="fixed inset-x-0 top-0 z-[var(--layer-app-update)]">
      <AppUpdateBanner />
    </div>,
    document.body
  )
}
